<!-- A wrapper component for the card components
     The size props for the card-list and card-component must be identical
     Size is required at the list level as it will be used for the styling of the flow of the cards
-->
<template>
  <!-- Additional wrapper to support container query -->
  <div class="card-list-container">
    <div v-bind="$attrs" :class="rootClasses">
      <!-- Place <card-component> in this slot -->
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

defineOptions({ inheritAttrs: false });

// Props
interface Props {
  size?: string;
  kind?: string;
}

const { size = "small", kind = "" } = defineProps<Props>();

// Computed
const rootClasses = computed(() => {
  return `card-list ${size} ${kind}`;
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/spacing";

.card-list-container {
  width: 100%;
  display: initial;
  container-type: inline-size;
}
.card-list {
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  margin: $spacing-06 0;

  &.grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: $spacing-06;

    @include media("tablet-min") {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @include media("desktop") {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @container (max-width: 788px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .card-component {
      //temporary reset width on card level, to be cleaned up once all card list successfully migrate to grid layout
      width: unset !important;
    }
  }

  &.standard {
    gap: $spacing-06;

    .content-subpage & {
      gap: $spacing-06;
    }

    > * {
      flex: 0 0 100%;
    }

    @include media("tablet-min") {
      > * {
        flex-basis: calc((100% - $spacing-06) / 2);
      }
    }

    @include media("desktop") {
      > * {
        flex-basis: calc((100% - $spacing-09) / 3);
      }
    }
  }

  &.white {
    padding: $spacing-05;
    background-color: $ui-01;
    border-radius: 10px;
  }

  &.small {
    @include media("tablet-max") {
      overflow-x: auto;
      flex-flow: row nowrap;
      gap: 0 $spacing-05;

      .content-bg-wrapper & {
        flex-wrap: wrap;
      }
    }

    @include media("desktop") {
      gap: $spacing-08;
    }
  }

  &.regular {
    gap: $spacing-07;
    flex-flow: row wrap;
  }

  &.xsmall,
  &.large {
    @include media("mobile") {
      gap: $spacing-08 0;
    }

    @include media("tablet") {
      gap: $spacing-08 $spacing-05;
    }

    @include media("desktop") {
      gap: $spacing-08;
    }
  }

  .content-subpage & {
    gap: $spacing-05;
  }

  .content-bg-wrapper & {
    margin: 0 0 $spacing-05 0;
  }

  > .bx--btn {
    margin: auto;
  }
}
</style>
